/* eslint-disable react-refresh/only-export-components -- TanStack Router route files export Route alongside their page component. */
import {
  DimeSeparator,
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableHead,
  DimeTableHeader,
  DimeTableRow,
  DimeTreeCombobox,
  type DimeTreeComboboxNode,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/tree-combobox')({
  component: TreeComboboxDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-background p-10">
      {children}
    </div>
  );
}

const CATEGORY_TREE: DimeTreeComboboxNode[] = [
  {
    id: 'flower',
    label: 'Flower',
    children: [
      {
        id: 'pre-roll',
        label: 'Pre-Roll',
        children: [{ id: 'single-pre-roll', label: 'Single Pre-Roll' }],
      },
      { id: 'eighths', label: 'Eighths' },
    ],
  },
  { id: 'pre-rolls', label: 'Pre-Rolls' },
  { id: 'edibles', label: 'Edibles', children: [{ id: 'gummies', label: 'Gummies' }] },
];

const PRODUCT_COUNTS = new Map([
  ['flower', 7],
  ['pre-roll', 4],
  ['single-pre-roll', 2],
  ['eighths', 3],
  ['pre-rolls', 0],
  ['edibles', 5],
  ['gummies', 5],
]);

const PATHS = new Map<string, string>();
const collectPaths = (nodes: readonly DimeTreeComboboxNode[], parentPath?: string) =>
  nodes.forEach((node) => {
    const path = parentPath ? `${parentPath} › ${node.label}` : node.label;
    PATHS.set(node.id, path);
    collectPaths(node.children ?? [], path);
  });
collectPaths(CATEGORY_TREE);

function triggerLabelFor(value: readonly string[]): string {
  if (value.length === 0) return 'All Categories';
  if (value.length === 1) return PATHS.get(value[0] ?? '') ?? '1 Category';
  return `${value.length} Categories`;
}

function CategoryPickerDemo({ withCounts = false }: Readonly<{ withCounts?: boolean }>) {
  const [value, setValue] = React.useState<string[]>(withCounts ? [] : ['pre-roll']);
  return (
    <DimeTreeCombobox
      roots={CATEGORY_TREE}
      value={value}
      onValueChange={setValue}
      triggerLabel={triggerLabelFor(value)}
      triggerTooltip={
        value.length > 1 ? value.map((id) => PATHS.get(id) ?? id).join('\n') : undefined
      }
      ariaLabel="Category"
      renderTrailing={withCounts ? (node) => PRODUCT_COUNTS.get(node.id) : undefined}
      isMuted={withCounts ? (node) => PRODUCT_COUNTS.get(node.id) === 0 : undefined}
    />
  );
}

const PROP_ROWS = [
  { prop: 'roots', type: 'DimeTreeComboboxNode[]', desc: 'Tree of { id, label, children? }.' },
  { prop: 'value', type: 'string[]', desc: 'Minimal set of explicitly checked ids.' },
  { prop: 'onValueChange', type: '(value: string[]) => void', desc: 'Receives the new value.' },
  { prop: 'triggerLabel', type: 'string', desc: 'Trigger text, computed by the consumer.' },
  { prop: 'triggerTooltip', type: 'string', desc: 'Hover text; newlines become line breaks.' },
  { prop: 'disabledTooltip', type: 'string', desc: 'Why it is disabled; hover, focus or tap.' },
  { prop: 'ariaLabel', type: 'string', desc: 'Names the search input and the tree.' },
  { prop: 'searchPlaceholder', type: 'string', desc: 'Defaults to "Search categories".' },
  { prop: 'emptyMessage', type: '(query) => string', desc: 'Shown when a search has no match.' },
  { prop: 'renderTrailing', type: '(node) => ReactNode', desc: 'Trailing content, e.g. counts.' },
  { prop: 'isMuted', type: '(node) => boolean', desc: 'Mutes a row; it stays selectable.' },
  { prop: 'clearLabel', type: 'string', desc: 'Clear button label; "Clear categories".' },
  { prop: 'disabled', type: 'boolean', desc: 'Disables the trigger and clear button.' },
  {
    prop: 'truncateLabel',
    type: "'end' | 'start'",
    desc: 'Where an overflowing label is cut; "start" keeps the leaf. Defaults to "end".',
  },
  { prop: 'className', type: 'string', desc: 'Wrapper classes; sets the width (w-44).' },
] as const;

function TreeComboboxDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Tree Combobox</h1>
      <p className="mb-8 text-muted-foreground">
        Multi-select picker for hierarchical data: a search input and an expandable checkbox tree in
        a popover. Checking a node includes its whole subtree.
      </p>

      <DocsSection title="Category picker">
        <Preview>
          <CategoryPickerDemo />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Chevrons expand without selecting. Pre-Roll starts checked, so expanding it shows Single
          Pre-Roll as included and disabled. Keyboard: ↓ ↑ move, → expands or enters, ← collapses or
          goes to the parent, Enter toggles, Esc closes.
        </p>
      </DocsSection>

      <DocsSection title="Counts and muted nodes">
        <Preview>
          <CategoryPickerDemo withCounts />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">renderTrailing</code> adds a count;{' '}
          <code className="font-mono">isMuted</code> greys out empty nodes, which stay selectable.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <DimeTable className="w-full text-sm">
          <DimeTableHeader>
            <DimeTableRow className="hover:bg-transparent">
              <DimeTableHead>Prop</DimeTableHead>
              <DimeTableHead>Type</DimeTableHead>
              <DimeTableHead>Description</DimeTableHead>
            </DimeTableRow>
          </DimeTableHeader>
          <DimeTableBody>
            {PROP_ROWS.map((row) => (
              <DimeTableRow key={row.prop}>
                <DimeTableCell className="font-mono text-xs">{row.prop}</DimeTableCell>
                <DimeTableCell className="font-mono text-xs text-muted-foreground">
                  {row.type}
                </DimeTableCell>
                <DimeTableCell className="text-muted-foreground">{row.desc}</DimeTableCell>
              </DimeTableRow>
            ))}
          </DimeTableBody>
        </DimeTable>
      </DocsSection>
    </div>
  );
}
